import React, { useState, useMemo, memo, useCallback } from 'react';
import { Loader } from '@Sage/hoi-poi-ui';
import { SectionList } from '@Sage/ssm-web5';
import { getLiteral } from 'utils/getLiteral';
import SenderFormModal from '../../SenderFormModal';
import SenderActionsCell from './tableCells/SenderActionsCell';
import SenderNameCell from './tableCells/SenderNameCell';
import SenderEmailCell from './tableCells/SenderEmailCell';
import SenderDomainVerifiedCell from './tableCells/SenderDomainVerifiedCell';
import AddressCell from './tableCells/AddressCell';
import NicknameCell from './tableCells/NicknameCell';

function SendersTable({ senders, loading, onRefresh }) {
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [senderToEdit, setSenderToEdit] = useState(null);

    const handleOpenEditModal = useCallback((sender) => {
        setSenderToEdit(sender);
        setIsEditModalOpen(true);
    }, []);

    const handleCloseEditModal = useCallback(() => {
        setIsEditModalOpen(false);
        setSenderToEdit(null);
    }, []);

    const handleEditSuccess = useCallback(() => {
        onRefresh?.();
        handleCloseEditModal();
    }, [onRefresh, handleCloseEditModal]);

    const columns = useMemo(
        () => [
            {
                colId: 'senderName',
                headerName: getLiteral('label_marketing_campaigns_sender_name'),
                field: 'name',
                cellRenderer: 'SenderNameCell',
                flex: 1,
                minWidth: 180,
            },
            {
                colId: 'senderEmail',
                headerName: getLiteral('label_marketing_campaigns_sender_email'),
                cellRenderer: 'SenderEmailCell',
                flex: 1,
                minWidth: 280,
            },

            {
                colId: 'address',
                headerName: getLiteral('label_address'),
                cellRenderer: 'AddressCell',
                flex: 1,
                minWidth: 250,
            },
            {
                colId: 'nickname',
                headerName: getLiteral('label_nickname'),
                field: 'nickName',
                cellRenderer: 'NicknameCell',
                width: 160,
            },
            {
                colId: 'domainVerified',
                headerName: getLiteral('label_marketing_campaigns_verified'),
                cellRenderer: 'SenderDomainVerifiedCell',
                width: 100,
                minWidth: 100,
                maxWidth: 120,
                suppressSizeToFit: true,
                resizable: false,
            },
            {
                colId: 'actions',
                headerName: '',
                cellRenderer: 'ActionsCell',
                cellRendererParams: {
                    senders,
                    onChangeDefault: onRefresh,
                    onDeleteSender: onRefresh,
                    onClickEditSender: handleOpenEditModal,
                },
                width: 140,
                suppressSizeToFit: true,
                resizable: false,
            },
        ],
        [senders, onRefresh, handleOpenEditModal],
    );

    const components = useMemo(
        () => ({
            SenderNameCell,
            SenderEmailCell,
            SenderDomainVerifiedCell,
            AddressCell,
            NicknameCell,
            ActionsCell: SenderActionsCell,
        }),
        [],
    );

    if (loading) {
        return (
            <div className="senders-table--loader">
                <Loader size="big" />
            </div>
        );
    }

    return (
        <div className="senders-table">
            <SectionList
                id="senders-table"
                className="senders-section-list"
                columns={columns}
                rows={senders}
                isLoading={loading}
                components={components}
                domLayout="normal"
                getRowHeight={() => 70}
                emptyViewProps={{
                    title: getLiteral('no_data_available'),
                }}
            />
            <SenderFormModal
                isOpen={isEditModalOpen}
                onCancel={handleCloseEditModal}
                onRequestClose={handleCloseEditModal}
                onSuccess={handleEditSuccess}
                senderToEdit={senderToEdit}
            />
        </div>
    );
}

SendersTable.displayName = 'SendersTable';

export default memo(SendersTable);
